/** @jsx jsx */
import { jsx } from '@emotion/react';

import React, { memo, SVGProps, useCallback, useContext, useEffect } from 'react';
import { FormattedChildBin, TimelineContext } from './timeline';
import { easeBounceOut, scaleLog, select, Series, stack, transition } from 'd3';
import { Colors } from '@blueprintjs/core';

const _Bars: React.FC<IProps> = ({ activeData = [] }) => {
	const {
		margin,
		data: rootData,
		dataHeight,
		xScale,
		yScale,
		svgRef,
		keys,
		colorScale,
		stackOrder,
	} = useContext(TimelineContext);

	const createBars = useCallback(
		(seriesName, series: Series<FormattedChildBin, string>[], colors) => {
			// Create a g element for each series (category)
			const category = select(svgRef.current)
				.select(`g.series-${seriesName}`)
				.selectAll('g.series')
				.data(series)
				.join('g')
				.classed('series', true)
				.style('fill', colors)
				.attr('height', dataHeight);

			// Create bars in each series (category) with the data for that category
			// Start y at bottom of graph and height to 0 for transitions
			const widthScale = scaleLog()
				.domain([5 / 0.75, 100])
				.range([0.75, 0.99])
				.clamp(true);

			let bars = category
				.selectAll('rect')
				.data((d: Series<FormattedChildBin, string>) => d)
				.join('rect')
				/* removed until transitions are fixed
		.attr('y', (d) => yScale(d[0]) || 0)
		.attr('height', 0) 
		*/
				.attr('y', (d) => {
					return yScale(d[1]) > 0 ? yScale(d[1]) : 0;
				})
				.attr('height', (d) => {
					let height = yScale(d[0]) - yScale(d[1]);
					return height && height > 0 ? height : 0;
				})
				.attr('x', (d) => xScale(d.data.start))
				// Shrink the width by logarithmically to give a reasonable bar width and gap
				.attr('width', (d) => {
					const width = xScale(d.data.end) - xScale(d.data.start);
					return width * widthScale(width);
				});

			// Add some bar bounce -- Will add this later
			//const trns = transition().duration(1000).delay(200).ease(easeBounceOut);
			//bars.transition(trns);

			return bars;
			/* removed until transistions fixed
				.attr('y', (d) => {
					return yScale(d[1]) > 0 ? yScale(d[1]) : 0;
				})
				.attr('height', (d) => {
					let height = yScale(d[0]) - yScale(d[1]);
					return height && height > 0 ? height : 0;
				}); */
		},
		[svgRef, xScale, yScale, dataHeight]
	);

	const getColor = useCallback((d: Series<FormattedChildBin, string>) => colorScale(d.key), [colorScale]);

	/* Data Data */
	useEffect(() => {
		// Stole this from StackedBarChart widget
		// The stack call produces a dense matrix of values (n rows by m colums)
		// in the case where there are many unique values amongst the rows this
		// is extremely poorly performing for no real reason.  So we filter out invalid
		// values and move on.
		const stackGen = stack<FormattedChildBin>()
			.keys(keys)
			.value((obj, key) => obj.values[key])
			.order(stackOrder);

		const rootStackedSeries = stackGen(rootData);
		const filteredRoot = filterStack(rootStackedSeries);
		createBars('root', filteredRoot, activeData?.length ? Colors.GRAY5 : getColor);

		const activeStackedSeries = stackGen(activeData);
		const filteredActive = filterStack(activeStackedSeries);
		createBars('active', filteredActive, getColor);
	}, [activeData, createBars, getColor, keys, rootData, stackOrder]);

	return (
		<React.Fragment>
			<g className="series-root" transform={`translate(${0}, ${margin.top})`} />
			<g className="series-active" transform={`translate(${0}, ${margin.top})`}></g>
		</React.Fragment>
	);
};

const filterStack = (stack) =>
	stack.map((bar) => {
		const retVal = bar.filter((segment) => {
			return !isNaN(segment[1]);
		});
		retVal.key = bar.key;
		retVal.index = bar.index;
		return retVal;
	});

export const Bars = memo(_Bars, (prevProps, nextProps) => {
	return prevProps.activeData === nextProps.activeData;
});

interface IProps extends SVGProps<SVGGElement> {
	activeData?: FormattedChildBin[];
}
